import React, { useEffect, useRef, useState } from "react";
import { CheckCircle, Play, Warning, XCircle } from "@phosphor-icons/react";
import {
  ALL_EVENTS, ALL_EVENTS_AUTO, AUTO_EVENTS, buildSandboxSrcdoc, snippetMounts,
} from "@/pages/landing/CustomTrackingLoader";

const PAGES = [{ surface: "landing", label: "Landing page" }, { surface: "thank_you", label: "Thank-you page" }];
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// The events real traffic sends this code on each page, in the order they happen.
function eventsFor(snip, surface) {
  const t = (snip.trigger || "").toLowerCase();
  if (t === ALL_EVENTS) return surface === "landing" ? [...ALL_EVENTS_AUTO.landing, "form_start"] : ALL_EVENTS_AUTO.thank_you;
  const all = surface === "landing" ? [...AUTO_EVENTS.landing, "form_start"] : AUTO_EVENTS.thank_you;
  return all.filter((e) => e === t);
}

function Summary({ sent, heard, calls, errors }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3 text-xs" data-testid="tracking-test-summary">
      <div className="border border-black p-2">
        <div className="label-mono mb-1">Events sent to your code</div>
        {sent.length ? sent.map((s) => (
          <div key={`${s.page}-${s.event}`} className="flex items-center gap-1" data-testid={`tracking-test-event-${s.event}`}>
            {heard[`${s.page}:${s.event}`] ? <CheckCircle size={12} weight="fill" className="text-emerald-700" /> : <XCircle size={12} weight="fill" className="text-zinc-400" />}
            <b className="font-mono">{s.event}</b>
            <span className="text-zinc-600">{heard[`${s.page}:${s.event}`] ? "heard" : "no listener for it"}</span>
          </div>
        )) : <div className="text-zinc-600">None</div>}
      </div>
      <div className="border border-black p-2">
        <div className="label-mono mb-1">Calls it tried to make</div>
        <div className="text-2xl font-black" data-testid="tracking-test-calls-count">{calls}</div>
        <div className="text-[10px] text-zinc-600">caught, not sent</div>
      </div>
      <div className={`border p-2 ${errors ? "border-red-700 bg-red-50" : "border-black"}`}>
        <div className="label-mono mb-1">Errors in your code</div>
        <div className="text-2xl font-black" data-testid="tracking-test-errors-count">{errors}</div>
      </div>
    </div>
  );
}

function LogRow({ e }) {
  const text = {
    skip: "Your code doesn't load on this page (its placement or trigger keeps it off).",
    none: `Nothing is sent to your code on this page (its trigger is "${e.trigger}").`,
    sent: `Sent ${e.event}`,
    listen: `Your code listens for ${e.event}`,
    event: `Your code got ${e.event}: ${e.listeners ? `${e.listeners} listener${e.listeners === 1 ? "" : "s"} ran` : "no listener for it"}${e.errors?.length ? ` · error: ${e.errors.join("; ")}` : ""}`,
    call: `Tried to send (${e.via}${e.method ? ` ${e.method}` : ""}): ${e.url}`,
    error: `Error: ${e.message}${e.source ? ` (${e.source})` : ""}`,
    ready: `Loaded. CustomTracking.context = ${JSON.stringify(e.context || {})}`,
  }[e.kind];
  const tone = e.kind === "error" || e.errors?.length ? "text-red-700" : e.kind === "call" ? "text-blue-800" : e.kind === "skip" || e.kind === "none" ? "text-zinc-500" : "";
  return (
    <li className={`px-2 py-1 font-mono text-[10px] break-all ${tone}`} data-testid={`tracking-test-log-${e.kind}`}>
      <span className="text-zinc-400">{e.page}</span> · {text}
    </li>
  );
}

// "Test my code": runs the code in the same locked sandbox as live pages, replays what a
// visitor does (page view, form start, lead submit, thank-you) and shows what the code heard.
export function TrackingTestModal({ snippet, onClose }) {
  const [log, setLog] = useState([]);
  const [running, setRunning] = useState(false);
  const [subId, setSubId] = useState("test-sub-123");
  const boxRef = useRef(null);
  const framesRef = useRef([]);

  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev.data;
      if (!d || d.type !== "lf-tracking-test") return;
      const f = framesRef.current.find((x) => x.iframe.contentWindow === ev.source);
      if (f) setLog((l) => [...l, { ...d, page: f.page }]);
    };
    window.addEventListener("message", onMsg);
    return () => {
      window.removeEventListener("message", onMsg);
      framesRef.current.forEach((f) => f.iframe.remove());
    };
  }, []);

  const run = async () => {
    framesRef.current.forEach((f) => f.iframe.remove());
    framesRef.current = [];
    setLog([]);
    setRunning(true);
    const add = (e) => setLog((l) => [...l, e]);
    const ctx = {
      landing_page_slug: snippet.landing_page_slug || "your-page", landing_page_id: snippet.landing_page_id || null,
      form_experience: "standard", experiment_variant: null, sub_id: subId.trim() || null,
      utm_source: "test", utm_campaign: "test", utm_medium: "test",
      session_id: `test-session-${Math.random().toString(36).slice(2, 8)}`,
    };
    for (const pg of PAGES) {
      if (!snippetMounts(snippet, pg.surface)) { add({ page: pg.label, kind: "skip" }); continue; }
      const iframe = document.createElement("iframe");
      iframe.setAttribute("sandbox", "allow-scripts");
      iframe.setAttribute("title", "tracking-test-sandbox");
      iframe.style.cssText = "position:absolute;width:0;height:0;border:0;visibility:hidden";
      framesRef.current.push({ iframe, page: pg.label });
      const loaded = new Promise((res) => iframe.addEventListener("load", res, { once: true }));
      iframe.srcdoc = buildSandboxSrcdoc(snippet, ctx, { test: true });
      boxRef.current?.appendChild(iframe);
      await Promise.race([loaded, wait(8000)]);
      await wait(800);
      const events = eventsFor(snippet, pg.surface);
      if (!events.length) add({ page: pg.label, kind: "none", trigger: snippet.trigger });
      for (const evt of events) {
        add({ page: pg.label, kind: "sent", event: evt });
        iframe.contentWindow?.postMessage({ type: "lf-tracking-event", event: evt, id: "test", context: { ...ctx, event: evt } }, "*");
        await wait(500);
      }
    }
    await wait(1200);
    setRunning(false);
  };

  const sent = log.filter((e) => e.kind === "sent");
  const heard = Object.fromEntries(log.filter((e) => e.kind === "event" && e.listeners > 0).map((e) => [`${e.page}:${e.event}`, true]));
  const errors = log.filter((e) => e.kind === "error" || e.errors?.length).length;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center overflow-y-auto p-6" data-testid="tracking-test-modal">
      <div className="bg-white border-2 border-black w-full max-w-3xl mt-8 mb-8">
        <div className="border-b border-black bg-black text-white px-5 py-3 flex items-center gap-3">
          <div className="font-chivo font-black text-xl uppercase truncate">Test my code{snippet.tracking_name ? ` · ${snippet.tracking_name}` : ""}</div>
          <button className="ml-auto text-xs uppercase tracking-widest" onClick={onClose} data-testid="tracking-test-close">Close</button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[12px] leading-relaxed">
            This runs your code in the same locked box it uses on live pages and plays out a visit: the landing page
            loads, the visitor starts the form, the lead is submitted and the thank-you page shows. You see which events
            your code got and what it tried to send.
          </p>
          <div className="flex flex-wrap items-end gap-2 text-xs">
            <label><span className="label-mono block">Test sub_id</span>
              <input value={subId} onChange={(e) => setSubId(e.target.value)} maxLength={80} className="border border-black px-2 py-1.5 font-mono" data-testid="tracking-test-sub" />
            </label>
            <button type="button" onClick={run} disabled={running} data-testid="tracking-test-run"
              className="border-2 border-emerald-800 bg-emerald-800 text-white px-4 py-2 text-[11px] uppercase tracking-widest hover:bg-emerald-900 disabled:opacity-50 inline-flex items-center gap-1">
              <Play size={12} weight="fill" /> {running ? "Running…" : log.length ? "Run again" : "Run test"}
            </button>
          </div>
          <div className="text-[11px] text-amber-900 bg-amber-50 border-l-2 border-amber-600 pl-3 py-2 flex gap-2">
            <Warning size={14} className="shrink-0 mt-0.5" />
            <span>Calls your code tries to make are caught and <b>not sent</b>, so your ad account gets no fake conversions.
              Images written straight into your code&apos;s HTML (like a &lt;noscript&gt; pixel) may still load.</span>
          </div>
          {log.length > 0 && <Summary sent={sent} heard={heard} calls={log.filter((e) => e.kind === "call").length} errors={errors} />}
          {log.length > 0 && (
            <ul className="border border-black divide-y divide-zinc-200 max-h-80 overflow-y-auto bg-zinc-50" data-testid="tracking-test-log">
              {log.map((e, i) => <LogRow key={i} e={e} />)}
            </ul>
          )}
          <div ref={boxRef} aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
